import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const FootTrafficReport = withListOverflow(FootTrafficListOwner, { exclude: '[data-list-nonrecord]' });
import React, { useEffect, useMemo, useState } from "react";
import { getFootTrafficReport } from "../../data/reportsClient.js";
import { T } from "../../app/tokens.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { ReportShell } from "./ReportShell.jsx";
import { ReportAccessNotice } from "./ReportShell.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { getReportNavEligibility } from "./reportNavEligibility.js";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(days) {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function formatNumber(value) {
  return value == null ? "No data" : Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function Card({ title, detail, children, testId, style }) {
  return (
    <section data-testid={testId} style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      overflow: "hidden",
      marginBottom: 14,
      ...style,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
        <strong style={{ color: T.dark, fontSize: T.fsMd5 }}>{title}</strong>
        {detail && <span style={{ color: T.light, fontSize: T.fsXs, textAlign: "right" }}>{detail}</span>}
      </div>
      {children}
    </section>
  );
}

function DailyChart({ daily }) {
  if (!daily.length) {
    return <div data-testid="foot-daily-no-data" style={{ color: T.light, padding: 28, textAlign: "center" }}>No data for this office and period.</div>;
  }
  const max = Math.max(...daily.map((row) => row.presentTotal), 1);
  return (
    <div style={{ padding: "16px 16px 12px" }}>
      <div data-testid="foot-daily-chart" aria-label="Daily all badge-ins chart" style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 180, borderBottom: `1px solid ${T.border}`, overflowX: "auto" }}>
        {daily.map((row) => {
          const height = Math.max(3, Math.round((row.presentTotal / max) * 142));
          return (
            <div key={row.date} data-testid={`foot-daily-bar-${row.date}`} title={`${row.date}: ${row.presentTotal.toLocaleString()} all badge-ins`} aria-label={`${row.date}: ${row.presentTotal.toLocaleString()} all badge-ins`} style={{ flex: "1 0 11px", minWidth: 11, height: 160, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
              <div style={{ width: "78%", height, minHeight: 3, background: row.isWeekend ? T.border : T.blue, borderRadius: "3px 3px 0 0" }} />
              <span style={{ color: T.light, fontSize: T.fsXxs, whiteSpace: "nowrap", transform: daily.length > 45 ? "rotate(-55deg)" : undefined }}>{row.date.slice(5)}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 12, color: T.mid, fontSize: T.fsXs }}>
        <span><i style={{ display: "inline-block", width: 10, height: 10, marginRight: 5, background: T.blue, borderRadius: 2 }} />Weekday</span>
        <span><i style={{ display: "inline-block", width: 10, height: 10, marginRight: 5, background: T.border, borderRadius: 2 }} />Weekend</span>
        <span>Hover a bar for the exact office-local date and count.</span>
      </div>
    </div>
  );
}

function WeekdayPattern({ rows }) {
  const max = Math.max(...rows.map((row) => row.average || 0), 1);
  return (
    <div data-testid="foot-weekday-pattern" style={{ padding: 16 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((row) => (
          <div key={row.weekday} data-testid={`foot-weekday-${row.label}`} style={{ display: "grid", gridTemplateColumns: "36px 1fr 52px", gap: 10, alignItems: "center", color: T.dark, fontSize: T.fsSm }}>
            <span>{row.label}</span>
            <div style={{ height: 16, background: T.lightGray, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: row.average == null ? 0 : `${Math.max(4, (row.average / max) * 100)}%`, height: "100%", background: row.isPeak ? T.blue : row.isWeekend ? T.border : T.teal, borderRadius: 4 }} />
            </div>
            <strong style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(row.average)}</strong>
          </div>
        ))}
      </div>
      <div style={{ color: T.mid, fontSize: T.fsXs, lineHeight: 1.5, marginTop: 12 }}>Busiest weekday is highlighted. Missing dates are excluded from the average, not counted as zero.</div>
    </div>
  );
}

function MarketRollup({ markets, company, periodLabel, marketsEnabled }) {
  return (
    <Card title={marketsEnabled ? "4 · By market" : "4 · Portfolio total"} detail={`Average daily · ${periodLabel}`} testId="foot-market-rollup" style={{ margin: 0 }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
          <thead><tr>{(marketsEnabled ? ["Market", "Avg daily", "Peak", "Busiest"] : ["Avg daily", "Peak", "Busiest"]).map((label, index) => <th key={label} style={{ padding: "9px 12px", textAlign: index ? "right" : "left", color: T.light, fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: ".04em", background: T.lightGray }}>{label}</th>)}</tr></thead>
          <tbody>
            {marketsEnabled && markets.map((row) => (
              <tr key={row.market} data-testid={`foot-market-row-${row.market}`}>
                <td style={{ padding: "9px 12px", borderBottom: `1px solid ${T.lightGray}`, color: T.dark, fontWeight: T.fwSemibold }}>{row.market}</td>
                <td style={{ padding: "9px 12px", borderBottom: `1px solid ${T.lightGray}`, textAlign: "right" }}>{formatNumber(row.avgDaily)}</td>
                <td style={{ padding: "9px 12px", borderBottom: `1px solid ${T.lightGray}`, textAlign: "right" }}>{formatNumber(row.peakTraffic)}</td>
                <td style={{ padding: "9px 12px", borderBottom: `1px solid ${T.lightGray}`, textAlign: "right" }}>{row.busiestWeekday || "No data"}</td>
              </tr>
            ))}
            {company && <tr data-testid="foot-company-row" style={{ background: T.blueSoft }}>
              {marketsEnabled && <td style={{ padding: "10px 12px", color: T.dark, fontWeight: T.fwBold }}>Company</td>}
              <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: T.fwBold }}>{formatNumber(company.avgDaily)}</td>
              <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: T.fwBold }}>{formatNumber(company.peakTraffic)}</td>
              <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: T.fwBold }}>{company.busiestWeekday || "No data"}</td>
            </tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function WeeklyGrid({ weeks, rows }) {
  const observed = rows.flatMap((row) => row.values.map((value) => value.average).filter((value) => value != null));
  const max = Math.max(...observed, 1);
  const background = (value) => value == null ? T.lightGray : `color-mix(in srgb, ${T.blue} ${Math.max(8, Math.round((value / max) * 70))}%, ${T.card})`;
  return (
    <Card title="3 · Weekly average per office" detail="Average daily by ISO week" testId="foot-weekly-grid">
      <div style={{ overflowX: "auto" }}>
        <table data-list-nonrecord="" style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
          <thead><tr>
            <th style={{ padding: "9px 12px", textAlign: "left", color: T.light, fontSize: T.fsXxs, background: T.lightGray }}>Office</th>
            {weeks.map((week) => <th key={week.key} style={{ padding: "9px 12px", textAlign: "right", color: T.light, fontSize: T.fsXxs, background: T.lightGray }}>{week.week}{week.isPartial ? " · partial" : ""}</th>)}
          </tr></thead>
          <tbody>{rows.map((row) => <tr key={row.officeId} data-testid={`foot-weekly-row-${row.officeId}`}>
            <td style={{ padding: "9px 12px", borderBottom: `1px solid ${T.lightGray}`, color: T.dark, fontWeight: T.fwSemibold }}>{row.office}</td>
            {row.values.map((value) => <td key={value.week} style={{ padding: "7px 10px", textAlign: "right", borderBottom: `1px solid ${T.lightGray}` }}>
              <span title={`${value.week}: ${formatNumber(value.average)} average daily all badge-ins`} style={{ display: "inline-block", minWidth: 48, padding: "4px 7px", borderRadius: 5, background: background(value.average), color: T.dark, fontVariantNumeric: "tabular-nums" }}>{formatNumber(value.average)}</span>
            </td>)}
          </tr>)}</tbody>
        </table>
      </div>
      <div style={{ padding: "10px 16px 13px", color: T.mid, fontSize: T.fsXs }}>Darker cells are busier. A current ISO week is labeled partial; absent office/week observations remain No data.</div>
    </Card>
  );
}

function FootTrafficListOwner({ onBack, prevReport, nextReport, onNavigateReport }) {
  const featureContext = useFeatureFlagContext();
  const eligibility = getReportNavEligibility(featureContext);
  const officesEnabled = eligibility.offices;
  const marketsEnabled = eligibility.markets;
  const [filters, setFilters] = useState({ from: daysAgoIso(89), to: todayIso(), officeId: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eligibility.ready || !officesEnabled) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFootTrafficReport(filters)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, eligibility.ready, officesEnabled]);

  const officeLabel = useMemo(() => data?.offices?.find((office) => office.officeId === data.selectedOfficeId), [data]);
  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value, ...(key === "officeId" ? {} : {}) }));
  const resetRange = () => setFilters((current) => ({ ...current, from: daysAgoIso(89), to: todayIso() }));

  if (!officesEnabled || !eligibility.ready) {
    return <ReportAccessNotice reportName="Foot Traffic" loading={!eligibility.ready} onBack={onBack} />;
  }

  return (
    <ReportShell
      recordNavigation
      onBack={onBack}
      prevReport={prevReport}
      nextReport={nextReport}
      onNavigateReport={onNavigateReport}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 0 40px", width: "100%" }}>
      <div data-testid="foot-controls" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, border: `1px solid ${T.border}`, background: T.card, borderRadius: 9, padding: "7px 10px", color: T.mid, fontSize: T.fsSm }}>
          Office
          <select data-testid="foot-office-select" value={data?.selectedOfficeId || ""} onChange={(event) => updateFilter("officeId", event.target.value)} style={{ border: 0, background: "transparent", color: T.dark, fontWeight: T.fwSemibold, outline: "none" }}>
            {data?.offices?.map((office) => <option key={office.officeId} value={office.officeId}>{office.office} ({office.officeId})</option>)}
          </select>
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 7, border: `1px solid ${T.border}`, background: T.card, borderRadius: 9, padding: "6px 10px", color: T.mid, fontSize: T.fsSm }}>
          From <input data-testid="foot-from-date" type="date" value={filters.from} onChange={(event) => updateFilter("from", event.target.value)} style={{ border: 0, color: T.dark, font: "inherit" }} />
          <span>to</span>
          <input data-testid="foot-to-date" type="date" value={filters.to} onChange={(event) => updateFilter("to", event.target.value)} style={{ border: 0, color: T.dark, font: "inherit" }} />
        </label>
        <button type="button" data-testid="foot-last-90" onClick={resetRange} style={{ border: `1px solid ${T.border}`, background: T.card, color: T.blue, borderRadius: 8, padding: "7px 10px", cursor: "pointer", fontWeight: T.fwSemibold }}>Last 90 days</button>
      </div>
      {data?.periodLabel && (
        <div data-testid="foot-period-label" style={{ color: T.mid, fontSize: T.fsSm, margin: "-8px 0 14px" }}>
          Selected date range: {data.periodLabel}
        </div>
      )}
      <div data-testid="foot-caveat" style={{ background: T.utilAmber, color: T.dark, border: `1px solid ${T.amber}`, borderRadius: 8, padding: "9px 12px", fontSize: T.fsSm, marginBottom: 16 }}>
        All badge-ins are shown as one total. Employee and visitor fields are not populated, so this report does not invent a split. {data?.currentWeek?.isPartial && "Current week is partial."}
      </div>
      {loading && <div data-testid="foot-loading" style={{ color: T.light, padding: 32, textAlign: "center" }}>Loading foot traffic…</div>}
      {error && !loading && <div data-testid="foot-error" role="alert" style={{ color: T.red, padding: 28, textAlign: "center" }}>Foot Traffic could not be loaded.</div>}
      {!loading && !error && data && (
        <>
          {!data.hasData && <div data-testid="foot-no-data" style={{ background: T.lightGray, border: `1px solid ${T.border}`, borderRadius: 9, color: T.mid, padding: "12px 14px", marginBottom: 14 }}>No data for {officeLabel?.office || data.selectedOfficeId || "this office"} in the selected period. A stored observed zero would still appear as 0.</div>}
          <div data-testid="foot-kpis" style={{ marginBottom: 16 }}>
            <KpiBand testId="foot-traffic-kpi-row" ariaLabel="Report metrics">
              <KpiTile
                label="Avg daily"
                value={formatNumber(data.kpis.avgDaily)}
                sub="all badge-ins per observed office-day"
                height={KPI_TILE_ROW_HEIGHT}
                testId="foot-kpi-avg-daily"
              />
              <KpiTile
                label="Busiest day"
                value={data.kpis.busiestWeekday || "No data"}
                sub={data.kpis.busiestWeekdayAverage == null ? "No observed weekday" : `average ${formatNumber(data.kpis.busiestWeekdayAverage)}`}
                height={KPI_TILE_ROW_HEIGHT}
                testId="foot-kpi-busiest-day"
              />
              <KpiTile
                label="Quietest weekday"
                value={data.kpis.quietestWeekday || "No data"}
                sub={data.kpis.quietestWeekdayAverage == null ? "No observed weekday" : `average ${formatNumber(data.kpis.quietestWeekdayAverage)}`}
                height={KPI_TILE_ROW_HEIGHT}
                testId="foot-kpi-quietest-weekday"
              />
              <KpiTile
                label="Peak day"
                value={formatNumber(data.kpis.peakDay)}
                sub={data.kpis.peakDate || "No observed day"}
                height={KPI_TILE_ROW_HEIGHT}
                testId="foot-kpi-peak-day"
              />
            </KpiBand>
          </div>
          <Card title={`1 · Daily counts${officeLabel ? ` · ${officeLabel.office}` : ""}`} detail="All badge-ins per office-local day" testId="foot-daily-card"><DailyChart daily={data.daily} /></Card>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 14, marginBottom: 14 }}>
            <Card title="2 · Day-of-week pattern" detail={officeLabel?.office || "Selected office"} testId="foot-weekday-card" style={{ margin: 0 }}><WeekdayPattern rows={data.weekdayPattern} /></Card>
            <MarketRollup markets={data.markets} company={data.company} periodLabel={data.periodLabel} marketsEnabled={marketsEnabled} />
          </div>
          <WeeklyGrid weeks={data.weeks} rows={data.weeklyRows} />
          <div data-testid="foot-source-note" style={{ color: T.light, fontSize: T.fsXs, lineHeight: 1.5 }}>Foot traffic is deduplicated at office/date grain from observed badge_presence_daily rows. It is distinct from RTO Compliance, which counts matched eligible employees.</div>
        </>
      )}
      </div>
    </ReportShell>
  );
}